import React, { useEffect, useState } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";

import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Filler,
    Legend,
} from "chart.js";

import { VectorMap } from "react-jvectormap";
import {
    analyticOvData,
    analyticAuData,
    worldMap,
    analyticOvDataSet2,
    analyticOvDataSet3,
    TrafficChannelDoughnutData,
    TrafficChannelDoughnutData2,
    TrafficChannelDoughnutData3,
    TrafficChannelDoughnutData4,
    deviceStatusData,
    deviceStatusDataSet2,
    deviceStatusDataSet3,
    deviceStatusDataSet4,
} from "./AnalyticsData";

// ✅ Place after all imports
Chart.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Filler, Legend);

export const AudienceLineChart = ({ state }) => {
  const [data, setData] = useState(analyticOvData);
  useEffect(() => {
    let object;
    if (state === "day-7") {
      object = analyticOvDataSet2;
    } else {
      object = analyticOvDataSet3;
    }
    setData(object);
  }, [state]);
  return (
    <Line
      className="analytics-line-large"
      data={data}
      options={{
        plugins: {
          legend: {
              display: false,
          },
          tooltip: {
              enabled: true,
              displayColors: false,
              backgroundColor: "#eff6ff",
              titleFont: {
                size: '13px',
              },
              titleColor: "#6783b8",
              titleMarginBottom: 6,
              bodyColor: "#9eaecf",
              bodyFont: {
                size: '12px',
              },
              bodySpacing: 4,
              padding: 10,
              footerMarginTop: 0,
          },
        },
        maintainAspectRatio: false,
        scales: {
          y:{
              display: true,
              ticks: {
                beginAtZero: false,
                color:"#9eaecf", 
                font: {
                  size: '11px',
                },
                padding: 8,
                stepSize: 2400,
              },
              grid: {
                color: "rgba(82, 100, 132, 0.2)",
                tickMarkLength: 0,
                zeroLineColor: "rgba(82, 100, 132,0.2)",
              },
          },
          x:{
              display: false,
              ticks: {
                color:"#9eaecf", 
                font: {
                  size: '12px',
                },
                source: "auto",
                padding: 0,
              },
              grid: {
                color: "transparent",
                tickMarkLength: 0,
                zeroLineColor: "transparent",
                offsetGridLines: true,
              },
          },
        },
      }}
    ></Line>
  );
};

export const AudienceLineChart2 = ({ waitTimeData, serviceTimeData }) => {
    const labels = waitTimeData.map(item => `${item[0]}:00`); // e.g., "8:00", "9:00", etc.
    const waitValues = waitTimeData.map(item => item[1]);
    const serviceValues = serviceTimeData.map(item => item[1]);

    const data = {
        labels,
        datasets: [
            {
                label: "Wait time Exceptions",
                data: waitValues,
                borderColor: "#798bff",
                backgroundColor: "rgba(121, 139, 255, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#798bff",
            },
            {
                label: "Service time Exceptions",
                data: serviceValues,
                borderColor: "#4ade80",
                backgroundColor: "rgba(74, 222, 128, 0)", // No fill
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#4ade80",
            },
        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: true, position: "top" },
            tooltip: {
                enabled: true,
                displayColors: false,
                backgroundColor: "#eff6ff",
                titleFont: { size: 13 },
                titleColor: "#6783b8",
                bodyColor: "#9eaecf",
                bodyFont: { size: 12 },
                bodySpacing: 4,
                padding: 10,
                footerMarginTop: 0,
            },
        },
        maintainAspectRatio: false,
        scales: {
            y: {
                display: true,
                beginAtZero: false,
                ticks: {
                    color: "#9eaecf",
                    font: { size: 11 },
                    padding: 8,
                },
                grid: {
                    color: "rgba(82, 100, 132, 0.2)",
                },
            },
            x: {
                ticks: {
                    color: "#9eaecf",
                    font: { size: 12 },
                },
                grid: {
                    color: "transparent",
                },
            },
        },
    };

    return <Line data={data} options={options} />;
};

export const AudienceLineChart3 = ({ waitTimeData, serviceTimeData,waitingException,serviceException }) => {
    // Convert to plain JS objects for quick lookup
    const waitMap = Object.fromEntries(waitTimeData);
    const serviceMap = Object.fromEntries(serviceTimeData);
    const waitingExceptionMap = Object.fromEntries(waitingException);
    const serviceExceptionMap = Object.fromEntries(serviceException);

    // Unique sorted hours
    const allHoursSet = new Set([
        ...waitTimeData.map(item => item[0]),
        ...serviceTimeData.map(item => item[0]),
        ...waitingException.map(item => item[0]),
        ...serviceException.map(item => item[0]),
    ]);
    const allHours = Array.from(allHoursSet).sort((a, b) => a - b);

    // Prepare chart data
    const labels = allHours.map(hour => `${hour}:00`);
    const waitValues = allHours.map(hour => waitMap[hour] ?? 0);
    const serviceValues = allHours.map(hour => serviceMap[hour] ?? 0);
    const waitExceptionValues = allHours.map(hour => waitingExceptionMap[hour] ?? 0);
    const serviceExceptionValues = allHours.map(hour => serviceExceptionMap[hour] ?? 0);

    const data = {
        labels,
        datasets: [
            {
                label: "Issued Tickets",
                data: waitValues,
                borderColor: "#798bff",
                backgroundColor: "rgba(121, 139, 255, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#798bff",
            },
            {
                label: "Served Tickets",
                data: serviceValues,
                borderColor: "#4ade80",
                backgroundColor: "rgba(74, 222, 128, 0)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#4ade80",
            }, {
                label: "Wait time Exceptions",
                data: waitExceptionValues,
                borderColor: "#f97316", // Bright orange
                backgroundColor: "rgba(249, 115, 22, 0.2)", // Light orange fill (optional)
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },
            {
                label: "Service time Exceptions",
                data: serviceExceptionValues,
                borderColor: "#facc15", // Yellow (e.g., Tailwind's yellow-400)
                backgroundColor: "rgba(250, 204, 21, 0.2)", // Light yellow fill
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },

        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: true, position: "top" },
            tooltip: {
                enabled: true,
                displayColors: false,
                backgroundColor: "#eff6ff",
                titleFont: { size: 13 },
                titleColor: "#6783b8",
                bodyColor: "#9eaecf",
                bodyFont: { size: 12 },
                bodySpacing: 4,
                padding: 10,
            },
        },
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: false,
                ticks: { color: "#9eaecf", font: { size: 11 }, padding: 8 },
                grid: { color: "rgba(82, 100, 132, 0.2)" },
            },
            x: {
                ticks: { color: "#9eaecf", font: { size: 12 } },
                grid: { color: "transparent" },
            },
        },
    };

    return <Line data={data} options={options} />;
};


export const AudienceLineChart4 = ({ waitTimeData, serviceTimeData,waitingException,serviceException }) => {
    // Convert to plain JS objects for quick lookup
    const waitMap = Object.fromEntries(waitTimeData);
    const serviceMap = Object.fromEntries(serviceTimeData);
    const waitingExceptionMap = Object.fromEntries(waitingException);
    const serviceExceptionMap = Object.fromEntries(serviceException);

    // Unique sorted hours
    const allHoursSet = new Set([
        ...waitTimeData.map(item => item[0]),
        ...serviceTimeData.map(item => item[0]),
        ...waitingException.map(item => item[0]),
        ...serviceException.map(item => item[0]),
    ]);
    const allHours = Array.from(allHoursSet).sort((a, b) => a - b);

    // Prepare chart data
    const labels = allHours.map(hour => `${hour}`);
    const waitValues = allHours.map(hour => waitMap[hour] ?? 0);
    const serviceValues = allHours.map(hour => serviceMap[hour] ?? 0);
    const waitExceptionValues = allHours.map(hour => waitingExceptionMap[hour] ?? 0);
    const serviceExceptionValues = allHours.map(hour => serviceExceptionMap[hour] ?? 0);

    const data = {
        labels,
        datasets: [
            {
                label: "Issued Tickets",
                data: waitValues,
                borderColor: "#798bff",
                backgroundColor: "rgba(121, 139, 255, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#798bff",
            },
            {
                label: "Served Tickets",
                data: serviceValues,
                borderColor: "#4ade80",
                backgroundColor: "rgba(74, 222, 128, 0)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#4ade80",
            }, {
                label: "Wait time Exceptions",
                data: waitExceptionValues,
                borderColor: "#f97316", // Bright orange
                backgroundColor: "rgba(249, 115, 22, 0.2)", // Light orange fill (optional)
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },
            {
                label: "Service time Exceptions",
                data: serviceExceptionValues,
                borderColor: "#facc15", // Yellow (e.g., Tailwind's yellow-400)
                backgroundColor: "rgba(250, 204, 21, 0.2)", // Light yellow fill
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },

        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: true, position: "top" },
            tooltip: {
                enabled: true,
                displayColors: false,
                backgroundColor: "#eff6ff",
                titleFont: { size: 13 },
                titleColor: "#6783b8",
                bodyColor: "#9eaecf",
                bodyFont: { size: 12 },
                bodySpacing: 4,
                padding: 10,
            },
        },
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: false,
                ticks: { color: "#9eaecf", font: { size: 11 }, padding: 8 },
                grid: { color: "rgba(82, 100, 132, 0.2)" },
            },
            x: {
                ticks: { color: "#9eaecf", font: { size: 12 } },
                grid: { color: "transparent" },
            },
        },
    };

    return <Line data={data} options={options} />;
};


export const AudienceLineChart45 = ({ waitTimeData, serviceTimeData, waitingException, serviceException }) => {
    // Map month numbers (1-12) to month names
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Extract all unique month numbers from all data arrays
    const allMonthsSet = new Set([
        ...waitTimeData.map(item => item[0]),
        ...serviceTimeData.map(item => item[0]),
        ...waitingException.map(item => item[0]),
        ...serviceException.map(item => item[0]),
    ]);

    // Sort months ascending (1 to 12)
    const allMonths = Array.from(allMonthsSet).sort((a, b) => a - b);

    // Create label array by converting month numbers to month names
    const labels = allMonths.map(monthNum => monthNames[monthNum - 1] || monthNum);

    // Convert arrays to lookup maps for quick value access by month number
    const waitMap = Object.fromEntries(waitTimeData);
    const serviceMap = Object.fromEntries(serviceTimeData);
    const waitingExceptionMap = Object.fromEntries(waitingException);
    const serviceExceptionMap = Object.fromEntries(serviceException);

    // Get values in the order of sorted months, default 0 if no value
    const waitValues = allMonths.map(month => waitMap[month] ?? 0);
    const serviceValues = allMonths.map(month => serviceMap[month] ?? 0);
    const waitExceptionValues = allMonths.map(month => waitingExceptionMap[month] ?? 0);
    const serviceExceptionValues = allMonths.map(month => serviceExceptionMap[month] ?? 0);

    // Chart data config
    const data = {
        labels,
        datasets: [
            {
                label: "Issued Tickets",
                data: waitValues,
                borderColor: "#798bff",
                backgroundColor: "rgba(121, 139, 255, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#798bff",
            },
            {
                label: "Served Tickets",
                data: serviceValues,
                borderColor: "#4ade80",
                backgroundColor: "rgba(74, 222, 128, 0)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#4ade80",
            },
            {
                label: "Wait time Exceptions",
                data: waitExceptionValues,
                borderColor: "#f97316",
                backgroundColor: "rgba(249, 115, 22, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },
            {
                label: "Service time Exceptions",
                data: serviceExceptionValues,
                borderColor: "#facc15",
                backgroundColor: "rgba(250, 204, 21, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#facc15",
            },
        ],
    };

    // Chart options config
    const options = {
        responsive: true,
        plugins: {
            legend: { display: true, position: "top" },
            tooltip: {
                enabled: true,
                displayColors: false,
                backgroundColor: "#eff6ff",
                titleFont: { size: 13 },
                titleColor: "#6783b8",
                bodyColor: "#9eaecf",
                bodyFont: { size: 12 },
                bodySpacing: 4,
                padding: 10,
            },
        },
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: false,
                ticks: { color: "#9eaecf", font: { size: 11 }, padding: 8 },
                grid: { color: "rgba(82, 100, 132, 0.2)" },
            },
            x: {
                ticks: { color: "#9eaecf", font: { size: 12 } },
                grid: { color: "transparent" },
            },
        },
    };

    return <Line data={data} options={options} />;
};


export const AudienceLineChart44 = ({ waitTimeData, serviceTimeData,waitingException,serviceException }) => {
    // Convert to plain JS objects for quick lookup
    const waitMap = Object.fromEntries(waitTimeData);
    const serviceMap = Object.fromEntries(serviceTimeData);
    const waitingExceptionMap = Object.fromEntries(waitingException);
    const serviceExceptionMap = Object.fromEntries(serviceException);

    // Unique sorted hours
    const allHoursSet = new Set([
        ...waitTimeData.map(item => item[0]),
        ...serviceTimeData.map(item => item[0]),
        ...waitingException.map(item => item[0]),
        ...serviceException.map(item => item[0]),
    ]);
    const allHours = Array.from(allHoursSet).sort((a, b) => a - b);

    // Prepare chart data
    const labels = allHours.map(dateStr => {
        // Expecting format like "2025-04-06"
        const parts = dateStr.split("-");
        return parts[2]; // just the day part, e.g., "06"
    });

    const waitValues = allHours.map(hour => waitMap[hour] ?? 0);
    const serviceValues = allHours.map(hour => serviceMap[hour] ?? 0);
    const waitExceptionValues = allHours.map(hour => waitingExceptionMap[hour] ?? 0);
    const serviceExceptionValues = allHours.map(hour => serviceExceptionMap[hour] ?? 0);

    const data = {
        labels,
        datasets: [
            {
                label: "Issued Tickets",
                data: waitValues,
                borderColor: "#798bff",
                backgroundColor: "rgba(121, 139, 255, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#798bff",
            },
            {
                label: "Served Tickets",
                data: serviceValues,
                borderColor: "#4ade80",
                backgroundColor: "rgba(74, 222, 128, 0)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#4ade80",
            }, {
                label: "Wait time Exceptions",
                data: waitExceptionValues,
                borderColor: "#f97316", // Bright orange
                backgroundColor: "rgba(249, 115, 22, 0.2)", // Light orange fill (optional)
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },
            {
                label: "Service time Exceptions",
                data: serviceExceptionValues,
                borderColor: "#facc15", // Yellow (e.g., Tailwind's yellow-400)
                backgroundColor: "rgba(250, 204, 21, 0.2)", // Light yellow fill
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },

        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: true, position: "top" },
            tooltip: {
                enabled: true,
                displayColors: false,
                backgroundColor: "#eff6ff",
                titleFont: { size: 13 },
                titleColor: "#6783b8",
                bodyColor: "#9eaecf",
                bodyFont: { size: 12 },
                bodySpacing: 4,
                padding: 10,
            },
        },
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: false,
                ticks: { color: "#9eaecf", font: { size: 11 }, padding: 8 },
                grid: { color: "rgba(82, 100, 132, 0.2)" },
            },
            x: {
                ticks: { color: "#9eaecf", font: { size: 12 } },
                grid: { color: "transparent" },
            },
        },
    };

    return <Line data={data} options={options} />;
};


export const AudienceLineChart4444 = ({ waitTimeData, serviceTimeData,waitingException,serviceException }) => {
    // Convert to plain JS objects for quick lookup
    const waitMap = Object.fromEntries(waitTimeData);
    const serviceMap = Object.fromEntries(serviceTimeData);
    const waitingExceptionMap = Object.fromEntries(waitingException);
    const serviceExceptionMap = Object.fromEntries(serviceException);

    // Unique sorted hours
    const allHoursSet = new Set([
        ...waitTimeData.map(item => item[0]),
        ...serviceTimeData.map(item => item[0]),
        ...waitingException.map(item => item[0]),
        ...serviceException.map(item => item[0]),
    ]);
    const allHours = Array.from(allHoursSet).sort((a, b) => a - b);

    // const labels = allHours.map(dateStr => {
    //     const parts = dateStr.split("-");
    //     return `${parts[0]}-${parts[1]}`; // returns "2025-01", "2025-02", etc.
    // });

    const labels = allHours.map(dateStr => {
        const [year, month] = dateStr.split("-");
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return `${year}-${monthNames[parseInt(month) - 1]}`; // returns "2025-January"
    });



    const waitValues = allHours.map(hour => waitMap[hour] ?? 0);
    const serviceValues = allHours.map(hour => serviceMap[hour] ?? 0);
    const waitExceptionValues = allHours.map(hour => waitingExceptionMap[hour] ?? 0);
    const serviceExceptionValues = allHours.map(hour => serviceExceptionMap[hour] ?? 0);

    const data = {
        labels,
        datasets: [
            {
                label: "Issued Tickets",
                data: waitValues,
                borderColor: "#798bff",
                backgroundColor: "rgba(121, 139, 255, 0.2)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#798bff",
            },
            {
                label: "Served tickets",
                data: serviceValues,
                borderColor: "#4ade80",
                backgroundColor: "rgba(74, 222, 128, 0)",
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#4ade80",
            }, {
                label: "Wait time Exceptions",
                data: waitExceptionValues,
                borderColor: "#f97316", // Bright orange
                backgroundColor: "rgba(249, 115, 22, 0.2)", // Light orange fill (optional)
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },
            {
                label: "Service time Exceptions",
                data: serviceExceptionValues,
                borderColor: "#facc15", // Yellow (e.g., Tailwind's yellow-400)
                backgroundColor: "rgba(250, 204, 21, 0.2)", // Light yellow fill
                fill: false,
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 3,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#f97316",
            },

        ],
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { display: true, position: "top" },
            tooltip: {
                enabled: true,
                displayColors: false,
                backgroundColor: "#eff6ff",
                titleFont: { size: 13 },
                titleColor: "#6783b8",
                bodyColor: "#9eaecf",
                bodyFont: { size: 12 },
                bodySpacing: 4,
                padding: 10,
            },
        },
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: false,
                ticks: { color: "#9eaecf", font: { size: 11 }, padding: 8 },
                grid: { color: "rgba(82, 100, 132, 0.2)" },
            },
            x: {
                ticks: { color: "#9eaecf", font: { size: 12 } },
                grid: { color: "transparent" },
            },
        },
    };

    return <Line data={data} options={options} />;
};


export const ActiveUserBarChart = ({ chartData }) => {
    return (
        <Bar
            data={{
                labels: chartData.map(item => `${item[0].toString().padStart(2, '0')}:00`),  // ['08:00', '09:00', ...]
                datasets: [
                    {
                        label: "Active Users",
                        data: chartData.map(item => item[1]),
                        backgroundColor: "rgba(99, 132, 255, 0.6)",
                        borderRadius: 4,
                        barThickness: 18,
                    },
                ],
            }}
            options={{
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            label: (context) => `${context.parsed.y} Users`,
                        },
                        backgroundColor: "#eff6ff",
                        titleFont: {
                            size: 10,
                        },
                        titleColor: "#6783b8",
                        bodyColor: "#9eaecf",
                        bodyFont: {
                            size: 10,
                        },
                        padding: 8,
                    },
                },
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: false,
                        ticks: {
                            color: "#9eaecf",
                            font: {
                                size: 12,
                            },
                            callback: (value) => `${value}`,
                        },
                        grid: {
                            color: "rgba(82, 100, 132, 0.2)",
                            tickMarkLength: 0,
                        },
                    },
                    x: {
                        ticks: {
                            color: "#9eaecf",
                            font: {
                                size: 12,
                            },
                        },
                        grid: {
                            color: "transparent",
                            tickMarkLength: 0,
                        },
                    },
                },
            }}
        />
  );
};

export const WPCharts = ({ data, className }) => {
  return (
    <Line
      className={className}
      data={data}
      options={{
        plugins: {
          legend: {
              display: false,
          },
          tooltip: {
              enabled: true,
              displayColors: false,
              backgroundColor: "#eff6ff",
              titleFont: {
                size: '9px',
              },
              titleColor: "#6783b8",
              titleMarginBottom: 6,
              bodyColor: "#9eaecf",
              bodyFont: {
                size: '9px',
              },
              bodySpacing: 4,
              padding: 6,
              footerMarginTop: 0,
              callbacks: {
                title: function () {
                  return false; 
                },
                label: function (context) {
                    return context.parsed.y;
                },
              },
          },
        },
        maintainAspectRatio: false,
        scales: {
          y:{
              display: false,
              ticks: {
                beginAtZero: false,
                padding: 0,
                stepSize: 300,
              },
          },
          x:{
              display: false,
              ticks: {
                source: "auto",
                padding: 0,
              },
              grid: {
                offsetGridLines: true,
              },
          },
        },
      }}
    ></Line>
  );
};

export const TCDoughnut = ({ chartData, className }) => {
    const [data, setData] = useState({
        labels: [],
        datasets: [
            {
                data: [],
                backgroundColor: [],
                borderWidth: 2,
                borderColor: "#fff",
            },
        ],
    });

    useEffect(() => {
        if (chartData && chartData.length > 0) {
            const labels = chartData.map(item => item[0]);
            const values = chartData.map(item => item[1]);
            const backgroundColor = [
                "#b695ff", "#798bff", "#f4aaa4", "#ffa9ce", "#98e0ff", "#f0bfff"
            ].slice(0, chartData.length); // use only as many colors as needed

            setData({
                labels,
                datasets: [
                    {
                        data: values,
                        backgroundColor,
                        borderWidth: 2,
                        borderColor: "#fff",
                    },
                ],
            });
        }
    }, [chartData]);

    return (
        <Doughnut
            className={className}
            data={data}
            options={{
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        displayColors: false,
                        backgroundColor: "#eff6ff",
                        titleFont: {
                            size: '13px',
                        },
                        titleColor: "#6783b8",
                        titleMarginBottom: 6,
                        bodyColor: "#9eaecf",
                        bodyFont: {
                            size: '12px',
                        },
                        bodySpacing: 4,
                        padding: 10,
                        footerMarginTop: 0,
                    },
                },
                rotation: -1.5,
                cutoutPercentage: 70,
                maintainAspectRatio: false,
            }}
        />
    );
};

export const SessionDoughnut = ({ state, className }) => {
  const [data, setData] = useState(deviceStatusData);
  useEffect(() => {
    let filteredData;
    if (state === "7") {
      filteredData = deviceStatusDataSet2;
    } else if (state === "15") {
      filteredData = deviceStatusDataSet3;
    } else {
      filteredData = deviceStatusDataSet4;
    }
    setData(filteredData);
  }, [state]);
  return (
    <Doughnut
      className={className}
      data={data}
      options={{
        plugins: {
          legend: {
              display: false,
          },
          tooltip: {
              enabled: true,
              displayColors: false,
              backgroundColor: "#eff6ff",
              titleFont: {
                size: '13px',
              },
              titleColor: "#6783b8",
              titleMarginBottom: 6,
              bodyColor: "#9eaecf",
              bodyFont: {
                size: '12px',
              },
              bodySpacing: 4,
              padding: 10,
              footerMarginTop: 0,
          },
        },
        rotation: -1.5,
        cutoutPercentage: 70,
        maintainAspectRatio: false,
      }}
    ></Doughnut>
  );
};

export const Map = ({ set }) => {
  return (
    <VectorMap
      map={"world_mill"}
      backgroundColor="transparent"
      borderColor="#dee6ed"
      borderOpacity={1}
      height={250}
      borderWidth={1}
      color="#ccd7e2"
      containerClassName="vector-map"
      zoomButtons={false}
      zoomOnScroll={false}
      tooltip={true}
      regionStyle={{
        initial: {
          fill: "#e4e4e4",
          "fill-opacity": 0.9,
          stroke: "none",
          "stroke-width": 0,
          "stroke-opacity": 0,
        },
        hover: {
          "fill-opacity": 0.8,
          hoverColor: "#9cabff",
          hoverOpacity: null,
          cursor: "pointer",
        },
        selectedHover: {},
      }}
      series={{
        regions: [
          {
            values: set === "30" ? worldMap.data2 : set === "7" ? worldMap.data3 : worldMap.data4,
            scale: ["#ccd7e2", "#798bff"],
            normalizeFunction: "polynomial",
          },
        ],
      }}
    ></VectorMap>
  );
};
